import {
    FilePdfOutlined, FileWordOutlined, FileExcelOutlined,
    FileImageOutlined, FileZipOutlined, FileTextOutlined, FileOutlined,
} from '@ant-design/icons';

export const mimeTypeToExtension = {
    // Images
    "image/jpeg": ".jpg",
    "image/png": ".png",
    "image/gif": ".gif",
    "image/bmp": ".bmp",
    "image/webp": ".webp",
    "image/svg+xml": ".svg",
    // PDF
    "application/pdf": ".pdf",
    // Word
    "application/msword": ".doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
    // Excel
    "application/vnd.ms-excel": ".xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
    // PowerPoint
    "application/vnd.ms-powerpoint": ".ppt",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": ".pptx",
    // Text / data
    "text/plain": ".txt",
    "text/html": ".html",
    "text/csv": ".csv",
    "application/json": ".json",
    "application/xml": ".xml"
};

export const ExtensionToMimeType = {
    ".jpg": "image/jpeg",
    ".png": "image/png",
    ".pdf": "application/pdf", // octet-stream ignored (duplicate)
    ".doc": "application/msword",
    ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".xls": "application/vnd.ms-excel",
    ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ".ppt": "application/vnd.ms-powerpoint",
    ".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ".txt": "text/plain",
    ".html": "text/html",
    ".csv": "text/csv",
    ".json": "application/json",
    ".xml": "application/xml",
    ".svg": "image/svg+xml",
    ".gif": "image/gif",
    ".bmp": "image/bmp",
    ".webp": "image/webp"
}

export const FileCategory = Object.freeze({
    Image: "Image",
    Pdf: "Pdf",
    Word: "Word",
    Excel: "Excel",
    Zip: "Zip",
    Unknown: "Unknown"
});

/**
 * Get File Type from FileName
 * @param {string} fileName - FileName
 * @returns {string} - Returns the string.
 */
export function getFileCategory(fileName) {
    const ext = fileName?.toLowerCase().split(".").pop();

    if (!ext) {
        return FileCategory.Unknown;
    }

    if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(ext)) {
        return FileCategory.Image;
    }

    if (ext === "pdf") {
        return FileCategory.Pdf;
    }

    if (["doc", "docx"].includes(ext)) {
        return FileCategory.Word;
    }

    if (["xls", "xlsx"].includes(ext)) {
        return FileCategory.Excel;
    }

    if (ext === "zip") {
        return FileCategory.Zip;
    }

    return FileCategory.Unknown;
}

/**
 * Get File Extension from Type
 * @param {file} file - file
 * @returns {string} - Returns the string.
 */
export function getFileExtension(file) {

    const extension = file?.name?.toLowerCase().split(".").pop();
    return extension
        ? `.${extension}`
        : file?.type && mimeTypeToExtension[file.type]  !== null ? mimeTypeToExtension[file.type] : null;
}

/**
 * Get File Extension from Type
 * @param {string} extension - extension
 * @returns {string} - Returns the string.
 */
export function getFileMimeType(extension) {
    const mimeType = ExtensionToMimeType[extension];
    return mimeType
        ? mimeType
        : extension;
}

/**
 * Convert Bytes size to KB.
 * @param {int} int - number to Convert.
 * @param {string} unit - unit to be converted to.
 * @returns {string} - Returns the string.
 */
export function convertBytes(int, unit = "KB") {
    if (typeof int !== "number") {
        return "N/A";
    }

    let result = int;

    switch (unit.toUpperCase()) {
        case 'KB':
            result = (int / 1024).toFixed(2);
            break;
        case 'MB':
            result = (int / (1024 * 1024)).toFixed(2);
            break;
        case 'GB':
            result = (int / (1024 * 1024 * 1024)).toFixed(2);
            break;
        default:
            return "n/a";
    }

    return result.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Convert Bytes size to KB.
 * @param {string} content - Base 64 Content.
 * @param {string | null} fileName - Name of file.
 * @param {string | null} type - type of file.
 * @returns {string} - Physical File to the browser.
 */
export function downloadBase64File({content, fileName = "attachment", type = "application/pdf"}) {
    const byteCharacters = atob(content);
    const byteArray = new Uint8Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(i);
    }

    const blob = new Blob([byteArray], { type: type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);  // Ensures compatibility in some browsers
    a.click();
    document.body.removeChild(a);  // Clean up the DOM
    URL.revokeObjectURL(url);
}

/**
 * Convert a file (Blob or File) to a Base64 string.
 * @param {Blob|File} file - The file or blob to convert.
 * @returns {Promise<string>} A promise that resolves to a Base64-encoded string.
 * @throws {Error} If the file is not a Blob or File.
 */
export async function convertFileToBase64(file) {
    if (file instanceof Blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onloadend = () => {
                if (reader.result) {
                    // Extract the base64 string (excluding the Data URL prefix)
                    resolve(reader.result.split(",")[1]);
                } else {
                    reject(new Error("Failed to read file."));
                }
            };
            reader.onerror = () => reject(new Error("Error reading file."));
        });
    } else if (typeof file === "string" && file.startsWith("data:")) {
        // Already a Base64-encoded Data URL, extract Base64 part
        return file.split(",")[1];
    } else {
        throw new Error("Invalid file type. Expected Blob or base64 string.");
    }
}


/**
 * Returns an icon component based on file extension.
 * @param {string} fileName - The file name.
 * @param {string} className - The className.
 * @returns {JSX.Element} Icon component
 */
export function getFileIcon(fileName, className = "") {
  const ext = fileName?.split(".").pop()?.toLowerCase();

  switch (ext) {
    case "pdf":
      return <FilePdfOutlined className={`flex-shrink-0 text-sm ${className} text-red-500`} />;

    case "doc":
    case "docx":
      return <FileWordOutlined className={`flex-shrink-0 text-sm ${className} text-blue-500`} />;

    case "xls":
    case "xlsx":
      return <FileExcelOutlined className={`flex-shrink-0 text-sm ${className} text-green-500`} />;

    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
      return <FileImageOutlined className={`flex-shrink-0 text-sm ${className} text-purple-500`} />;

    case "zip":
    case "rar":
    case "7z":
      return <FileZipOutlined className={`flex-shrink-0 text-sm ${className} text-yellow-500`} />;

    case "txt":
    case "csv":
      return <FileTextOutlined className={`flex-shrink-0 text-sm ${className} text-gray-500`} />;

    default:
      return <FileOutlined className={`flex-shrink-0 text-sm ${className} text-gray-400`} />;
  }
}